import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useGroups } from "../hooks.ts";
import { auditParts, type AuditLookup } from "../lib/audit.ts";
import { Button, Card, cx, PageHeader, Spinner } from "../components/ui.tsx";
import { Icon } from "../components/icons.tsx";

const page = (before?: number) => unwrap(api.admin.audit.$get({ query: { limit: "50", ...(before ? { before: String(before) } : {}) } }));
type Entry = Awaited<ReturnType<typeof page>>["rows"][number];

export function AuditPage() {
  const q = useInfiniteQuery({
    queryKey: ["audit"],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => page(pageParam),
    getNextPageParam: (p) => p.next ?? undefined,
  });
  // People and groups by id, so the details name them (both lists are usually cached by the tabs next to this one).
  const users = useQuery({ queryKey: ["users"], queryFn: () => unwrap(api.admin.users.$get()) });
  const groups = useGroups();
  const lookup = useMemo<AuditLookup>(() => {
    const names = new Map<string, { label: string; name: string }>();
    for (const u of users.data ?? []) names.set(u.id, { label: "Person", name: u.email });
    for (const g of groups.data ?? []) names.set(g.id, { label: "Group", name: g.name });
    return (id) => names.get(id);
  }, [users.data, groups.data]);
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <>
      <PageHeader title="Audit log" description="Every sign-in and admin change, newest first." />
      <Card className="overflow-x-auto">
        {q.isPending ? (
          <div className="grid place-items-center py-16"><Spinner /></div>
        ) : (
          // From a tablet up it fits the card. On a phone it scrolls sideways, like the other admin tables, and is wider there so the details keep room.
          <table className="w-full min-w-[760px] text-sm sm:min-w-[640px]">
            <thead>
              <tr className="border-b border-[var(--line)] bg-[var(--panel-2)]/40 text-left text-[12.5px] font-medium whitespace-nowrap text-[var(--muted)]">
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Who</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <AuditEntry key={r.id} row={r} lookup={lookup} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {q.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>Load more</Button>
        </div>
      )}
    </>
  );
}

/** One entry: its details in words, with the JSON the server stored behind the pill at the end. */
function AuditEntry({ row, lookup }: { row: Entry; lookup: AuditLookup }) {
  const [open, setOpen] = useState(false);
  const parts = auditParts(row, lookup);
  const raw = Object.keys(row.details).length > 0;
  const at = new Date(row.at);
  // On a narrower screen the time goes under the date and the action breaks after its dots, leaving the room to the details.
  return (
    <>
      <tr className={cx("align-top", !open && "border-b border-[var(--line)] last:border-0")}>
        <td className="px-4 py-3 text-[var(--muted)]">
          <span className="whitespace-nowrap">{at.toLocaleDateString()}</span> <span className="whitespace-nowrap">{at.toLocaleTimeString()}</span>
        </td>
        <td className="px-4 py-3">{row.actorEmail ?? "system"}</td>
        <td className="px-4 py-3 font-mono text-xs">
          {row.action.split(".").map((s, i) => (
            <span key={i}>
              {i > 0 && <>.<wbr /></>}
              {s}
            </span>
          ))}
        </td>
        <td className="w-full px-4 py-3 text-[13px] [overflow-wrap:anywhere]">
          {/* Real spaces around the dots and before the pill, so lines break between entries, never inside a word. */}
          {parts.map((p, i) => (
            <span key={i}>
              {i > 0 && <span className="mx-0.5 text-[var(--muted)]/60">{"\u00a0· "}</span>}
              <span className="text-[var(--muted)]">{p.label}:</span> {p.value}
            </span>
          ))}
          {raw && parts.length > 0 && " "}
          {raw && (
            <button
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              aria-label={open ? "Hide the raw JSON" : "Show the raw JSON"}
              className={cx(
                "inline-flex h-5 items-center gap-0.5 rounded-full pr-1 pl-2 align-[1px] text-[11px] font-medium ring-1 ring-[var(--line)] ring-inset transition-colors",
                parts.length > 0 && "ml-1",
                open ? "bg-[var(--panel-2)] text-[var(--text)]" : "text-[var(--muted)] hover:bg-[var(--panel-2)] hover:text-[var(--text)]",
              )}
            >
              JSON
              <Icon name="chevron" size={12} className={cx("transition-transform duration-200", open && "rotate-90")} />
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-[var(--line)] last:border-0">
          <td colSpan={4} className="px-4 pb-4">
            <pre className="max-h-80 overflow-auto rounded-xl bg-[var(--panel-2)] px-4 py-3 font-mono text-xs leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{JSON.stringify(row.details, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}
